import React, { useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";

import { AuthenticationService } from "../../components/AuthenticationService";
import LocaleSelect from "../../components/Locale/LocaleSelect";
import PasswordTooltip from "./PasswordTooltip";
import faviconOixio from "../../css/img/favicon.png";

import "./LoginPage.css";


const PasswordResetPage = () => {
    const location = useLocation();
    const query = new URLSearchParams(location.search);

    const [submitting, setSubmitting] = useState(false);
    const [redirect, setRedirect] = useState(false);
    const [userId] = useState(query.get("user") || "");
    const [resetToken] = useState(query.get("token") || "");
    const [newPassword1, setNewPassword1] = useState("");
    const [newPassword2, setNewPassword2] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const { i18n, t } = useTranslation(["pages", "common"]);

    useEffect(() => {
        document.title = i18n.t("common:document.title");
    }, [i18n.language]);

    const handleChangeNew1 = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewPassword1(event.target.value);
    };

    const handleChangeNew2 = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewPassword2(event.target.value);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setSubmitting(true);
        setErrorMessage("");

        if (!resetToken) {
            setSubmitting(false);
            setErrorMessage(t("resetPassword.errorResetToken"));
            return;
        }

        if (!newPassword1 || !newPassword2) {
            setSubmitting(false);
            setErrorMessage(t("password.errorNewPassword1"));
            return;
        }

        if (newPassword1 !== newPassword2) {
            setSubmitting(false);
            setErrorMessage(t("password.errorNewPassword2"));
            return;
        }

        try {
            await AuthenticationService.resetPassword(
                userId,
                resetToken,
                newPassword1
            );

            setSubmitting(false);
            setRedirect(true);
        } catch (error) {
            setSubmitting(false);

            if (error === "Incorrect password.") {
                setErrorMessage(t("password.errorSubmit"));
            } else {
                setErrorMessage(t("password.errorComplexity"));
            }
        }
    };

    if (redirect) {
        return <Navigate replace to="/" />;
    }

    return (
        <div className="container-fluid">
            <Card className="login-clean no-border mt-5">
                <Card.Body>
                    <Row>
                        <form
                            method="post"
                            className="login-clean-form"
                            onSubmit={handleSubmit}
                            noValidate
                        >
                            <Row>
                                <Col className="mb-2 ms-auto">
                                    <LocaleSelect />
                                </Col>
                            </Row>
                            <Row className="justify-content-center">
                                <img
                                    src={faviconOixio}
                                    alt="oixio favicon"
                                    className="favicon_login"
                                />
                                <span className="logo-title">
                                    {t("resetPassword.heading")}
                                </span>
                            </Row>
                            <div className="form-group">
                                <PasswordTooltip id="newPassword1">
                                    <input
                                        id="newPassword1"
                                        className="form-control login-password-input mt-2"
                                        type="password"
                                        name="newPassword1"
                                        placeholder={t("password.newPassword1")}
                                        value={newPassword1}
                                        onChange={handleChangeNew1}
                                    />
                                </PasswordTooltip>
                            </div>
                            <div className="form-group">
                                <PasswordTooltip id="newPassword2">
                                    <input
                                        id="newPassword2"
                                        className="form-control login-password-input mt-2"
                                        type="password"
                                        name="newPassword2"
                                        placeholder={t("password.newPassword2")}
                                        value={newPassword2}
                                        onChange={handleChangeNew2}
                                    />
                                </PasswordTooltip>
                            </div>
                            <div className="form-group">
                                <Row>
                                    <button
                                        className="btn btn-login shadow-sm mt-3 mb-3"
                                        type="submit"
                                        disabled={submitting}
                                    >
                                        {t("password.submit")}
                                    </button>
                                </Row>
                                <Row className="ms-auto">
                                    <Col>
                                        <a
                                            href="/"
                                            className="btn btn-link btn-navigateBack btn-forgotPassword"
                                            role="button"
                                        >
                                            {t("login.back")}
                                        </a>
                                    </Col>
                                </Row>
                            </div>
                            <div className="input-error-login text-danger">{errorMessage}</div>
                        </form>
                    </Row>
                </Card.Body>
            </Card>
        </div>
    );
};

export default PasswordResetPage;
